// src/components/roads/drive/RoadVisionModelPanel.js
// --- UPDATE (Drive Mode AI review) -----------------------------------------------------
// The road-damage DETECTION model (computer vision used by Drive Mode), from GET /vision/stats:
//   • which detector answers (Roboflow workflow / FlowSense's own YOLO), engine online, active
//     model version and its accuracy on held-out photos (mAP50, precision, recall)
//   • the training set: photos per status (to review / confirmed / corrected / not damage) and
//     labelled photos per damage class, technician agreement with the AI
//   • "Train new model" (POST /vision/train, supervisors) — the ML engine trains on every labelled
//     photo, compares the new model with production on held-out photos and promotes it only if
//     it is not worse; the run history shows the decision
//   • autopilot: retrains by itself after enough new labels
// variant="mini" is a compact block for the AI Command Center.
// --- END UPDATE ------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, Icon, SimpleGrid, Progress, Tooltip, Table, Thead, Tbody, Tr, Th, Td,
    Alert, AlertIcon, AlertDescription, Skeleton, IconButton, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdModelTraining, MdRefresh, MdCheckCircle, MdCloudOff, MdCloudDone, MdAutorenew } from 'react-icons/md';
import { getVisionStats, startVisionTraining, damageInfo, pct, fmtWhen } from './driveAiApi';

const RUN_SCHEME = { queued: 'yellow', running: 'blue', succeeded: 'green', failed: 'red', skipped: 'gray' };
const STATUS_LABEL = { unreviewed: 'To review', confirmed: 'Confirmed', corrected: 'Corrected', rejected: 'Not damage' };
const STATUS_SCHEME = { unreviewed: 'yellow', confirmed: 'green', corrected: 'blue', rejected: 'gray' };

const metricOf = (m, k) => (m && m[k] != null && Number.isFinite(Number(m[k])) ? Number(m[k]) : null);

const RoadVisionModelPanel = ({ variant = 'full', embedded = false }) => {
    const toast = useToast();
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isTraining, setIsTraining] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const tile = useColorModeValue('gray.50', 'whiteAlpha.50');
    const headBg = useColorModeValue('gray.50', 'gray.900');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const load = useCallback(async (silent = false) => {
        if (!silent) setIsLoading(true);
        try {
            const s = await getVisionStats();
            if (!mountedRef.current) return;
            setStats(s); setError(null);
        } catch (e) {
            if (!mountedRef.current) return;
            setError(e.status === 403 ? 'Only office roles can see the detection model.'
                : e.status === 404 ? 'The detection-model API (/api/v1/roads/vision/stats) is not on this server yet — update backend/routes/roadRoutes.js.' : e.message);
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, []);
    useEffect(() => { load(false); }, [load]);

    const runs = stats?.training?.runs || [];
    const active = runs.find((r) => r.status === 'queued' || r.status === 'running');
    useEffect(() => {
        const t = setInterval(() => load(true), active ? 15000 : 120000);
        return () => clearInterval(t);
    }, [active, load]);

    const train = useCallback(async () => {
        setIsTraining(true);
        try {
            const r = await startVisionTraining();
            toast({ title: 'Training started', description: `Run ${r?.runId || ''} — ${r?.positives ?? '—'} labelled photo(s). The new model is promoted only if it scores at least as well as production.`, status: 'success', duration: 6000 });
            load(true);
        } catch (e) {
            toast({ title: 'Training not started', description: e.status === 403 ? 'Only supervisors, managers or admins can retrain the model.' : e.message, status: 'warning', duration: 6000 });
        } finally {
            if (mountedRef.current) setIsTraining(false);
        }
    }, [toast, load]);

    const engine = stats?.engine || {};
    const local = engine.local || {};
    const rf = engine.roboflow || {};
    const activeVersion = engine.active_version || local.version || null;
    const activeEntry = (engine.versions || []).find((v) => v.version === activeVersion) || null;
    const m = activeEntry?.metrics || local.metrics || null;
    const t = stats?.training || {};
    const labelled = t.labelledPositives ?? 0;
    const minTrain = t.minToTrain ?? 20;
    const canTrain = labelled >= minTrain && !active;
    const byStatus = (stats?.samples || []).reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + s.count; return acc; }, {});
    const detectorLabel = stats?.preferredDetector === 'local' ? 'FlowSense model' : stats?.preferredDetector === 'roboflow' ? 'Roboflow workflow' : 'Auto (best agreement)';

    const trainButton = (
        <Tooltip hasArrow label={active ? `Run ${active.id} is ${active.status}` : labelled < minTrain ? `Needs at least ${minTrain} labelled photos (${labelled} so far) — confirm Drive Mode photos to add more.` : 'Train on every labelled photo; promoted only if not worse than production'}>
            <Button size={variant === 'mini' ? 'xs' : 'sm'} colorScheme="purple" leftIcon={<Icon as={active ? MdAutorenew : MdModelTraining} />} onClick={train} isLoading={isTraining} isDisabled={!canTrain} w={variant === 'mini' ? 'full' : undefined}>
                {active ? `Training… (${active.status})` : 'Train new model'}
            </Button>
        </Tooltip>
    );

    if (variant === 'mini') {
        return (
            <Box>
                <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.500">Damage detection model</Text>
                    {stats && <Badge colorScheme={engine.online ? 'green' : 'red'} borderRadius="full" fontSize="9px">{engine.online ? 'engine online' : 'engine offline'}</Badge>}
                </HStack>
                {error ? <Text fontSize="2xs" color={muted}>{error}</Text> : isLoading && !stats ? <Skeleton h="40px" borderRadius="md" /> : (
                    <>
                        <Text fontSize="xs" color={muted} mb={1}>
                            {activeVersion ? `Own model ${activeVersion}` : 'No own model yet — Roboflow answers'} · mAP50 {pct(metricOf(m, 'map50'))}
                        </Text>
                        <Progress value={Math.min(100, (labelled / Math.max(1, minTrain)) * 100)} size="xs" borderRadius="full" colorScheme={labelled >= minTrain ? 'green' : 'purple'} mb={1} aria-label="Labelled photos" />
                        <Text fontSize="2xs" color={muted} mb={2}>{labelled} labelled photo(s){byStatus.unreviewed ? ` · ${byStatus.unreviewed} to review` : ''} · {t.newLabelsSinceLastRun ?? 0} new since last run</Text>
                        {trainButton}
                    </>
                )}
            </Box>
        );
    }

    return (
        <Box bg={embedded ? 'transparent' : cardBg} borderRadius="2xl" borderWidth={embedded ? 0 : '1px'} borderColor={borderColor} p={embedded ? 0 : 4} boxShadow={embedded ? 'none' : 'sm'} aria-label="Road damage detection model">
            <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                <HStack spacing={2}>
                    <Icon as={MdModelTraining} color="purple.500" />
                    <Text fontSize="sm" fontWeight="800">Road damage detection model</Text>
                    {stats && (
                        <Badge colorScheme={engine.online ? 'green' : 'red'} display="flex" alignItems="center" gap={1}>
                            <Icon as={engine.online ? MdCloudDone : MdCloudOff} />{engine.online ? 'ML engine online' : 'ML engine offline'}
                        </Badge>
                    )}
                </HStack>
                <HStack spacing={1}>
                    {trainButton}
                    <IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Refresh model status" onClick={() => load(false)} isLoading={isLoading} />
                </HStack>
            </Flex>

            {error && <Alert status="info" borderRadius="lg" mb={3} py={2}><AlertIcon /><AlertDescription fontSize="xs">{error}</AlertDescription></Alert>}
            {/* --- UPDATE (startup fix): server without the road-vision service */}
            {stats && stats.installed === false && (
                <Alert status="info" borderRadius="lg" mb={3} py={2}><AlertIcon /><AlertDescription fontSize="xs">{engine.error || 'Road-vision service is not installed on this server.'} Drive Mode photo counts are shown; training needs the road-vision service.</AlertDescription></Alert>
            )}
            {/* --- END UPDATE */}
            {isLoading && !stats && !error && <Skeleton h="120px" borderRadius="lg" />}

            {stats && (
                <VStack align="stretch" spacing={3}>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                        <Box bg={tile} p={2.5} borderRadius="lg">
                            <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={muted}>Detector in use</Text>
                            <Text fontSize="sm" fontWeight="800">{detectorLabel}</Text>
                            <Text fontSize="2xs" color={muted}>Roboflow {rf.configured ? (rf.circuit_open ? 'paused (errors)' : 'configured') : 'not configured'}</Text>
                        </Box>
                        <Box bg={tile} p={2.5} borderRadius="lg">
                            <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={muted}>FlowSense model</Text>
                            <Text fontSize="sm" fontWeight="800" fontFamily="mono">{activeVersion || 'not trained yet'}</Text>
                            <Text fontSize="2xs" color={muted}>{(engine.versions || []).length} version(s) on the engine</Text>
                        </Box>
                        <Tooltip hasArrow label="mAP50: how well boxes and classes match the reviewed photos it never trained on (higher is better). Precision: share of its detections that are real. Recall: share of real damage it finds.">
                            <Box bg={tile} p={2.5} borderRadius="lg" cursor="help">
                                <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={muted}>Accuracy (held-out)</Text>
                                <Text fontSize="sm" fontWeight="800">mAP50 {pct(metricOf(m, 'map50'))}</Text>
                                <Text fontSize="2xs" color={muted}>precision {pct(metricOf(m, 'precision'))} · recall {pct(metricOf(m, 'recall'))}</Text>
                            </Box>
                        </Tooltip>
                        <Box bg={tile} p={2.5} borderRadius="lg">
                            <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={muted}>Training data</Text>
                            <Text fontSize="sm" fontWeight="800">{labelled} labelled · need {minTrain}</Text>
                            <Progress value={Math.min(100, (labelled / Math.max(1, minTrain)) * 100)} size="xs" mt={1} borderRadius="full" colorScheme={labelled >= minTrain ? 'green' : 'purple'} aria-label="Labelled photos" />
                        </Box>
                    </SimpleGrid>

                    <Flex gap={3} direction={{ base: 'column', lg: 'row' }}>
                        <Box flex={1} bg={tile} p={3} borderRadius="lg">
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Photos in the training set</Text>
                            <HStack spacing={1} wrap="wrap" mb={2}>
                                {Object.keys(STATUS_LABEL).map((k) => <Badge key={k} colorScheme={STATUS_SCHEME[k]}>{STATUS_LABEL[k]} {byStatus[k] || 0}</Badge>)}
                            </HStack>
                            <VStack align="stretch" spacing={1}>
                                {[...(stats.perClass || [])].sort((a, b) => b.labelled - a.labelled).map((c) => (
                                    <HStack key={c.label} spacing={2}>
                                        <Box w={2} h={2} borderRadius="full" bg={damageInfo(c.label).color} />
                                        <Text fontSize="xs" flex={1}>{damageInfo(c.label).label}</Text>
                                        <Text fontSize="2xs" color={muted}>{c.labelled} labelled / {c.total}</Text>
                                    </HStack>
                                ))}
                                {!(stats.perClass || []).length && <Text fontSize="xs" color={muted}>No photos yet.</Text>}
                            </VStack>
                        </Box>
                        <Box flex={1} bg={tile} p={3} borderRadius="lg">
                            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>Do technicians agree with the AI?</Text>
                            {(stats.agreement || []).length === 0 ? <Text fontSize="xs" color={muted}>No reviewed AI detections yet.</Text> : (stats.agreement || []).map((a) => (
                                <Box key={a.source || 'unknown'} mb={1.5}>
                                    <Flex justify="space-between" fontSize="xs"><Text>{a.source === 'local' ? 'FlowSense model' : a.source === 'roboflow' ? 'Roboflow' : a.source || 'Unknown'}</Text><Text fontWeight="800">{pct(a.rate)}</Text></Flex>
                                    <Progress value={(a.rate || 0) * 100} size="xs" borderRadius="full" colorScheme={(a.rate || 0) >= 0.8 ? 'green' : (a.rate || 0) >= 0.6 ? 'yellow' : 'orange'} aria-label={`Agreement ${pct(a.rate)}`} />
                                    <Text fontSize="2xs" color={muted}>{a.agreed} of {a.reviewed} reviewed</Text>
                                </Box>
                            ))}
                            <Text fontSize="2xs" color={muted} mt={2}>
                                Autopilot {t.autopilot ? `on — retrains after ${t.autopilotThreshold ?? '—'} new labels` : 'off'} · {t.newLabelsSinceLastRun ?? 0} new label(s) since the last run.
                            </Text>
                        </Box>
                    </Flex>

                    <Box>
                        <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={1}>Training runs</Text>
                        {runs.length === 0 ? <Text fontSize="xs" color={muted}>No training runs yet.</Text> : (
                            <Box overflowX="auto" borderWidth="1px" borderColor={borderColor} borderRadius="lg">
                                <Table size="sm">
                                    <Thead bg={headBg}><Tr><Th>Started</Th><Th>Status</Th><Th>Trigger</Th><Th isNumeric>Photos</Th><Th>New model</Th><Th>mAP50 new / prod</Th><Th>Decision</Th></Tr></Thead>
                                    <Tbody>
                                        {runs.map((r) => (
                                            <Tr key={r.id}>
                                                <Td fontSize="xs" whiteSpace="nowrap">{fmtWhen(r.started_at)}</Td>
                                                <Td><Badge colorScheme={RUN_SCHEME[r.status] || 'gray'}>{r.status}</Badge></Td>
                                                <Td fontSize="xs">{r.trigger_source}{r.requested_by ? ` · ${r.requested_by}` : ''}</Td>
                                                <Td isNumeric fontSize="xs">{r.samples ?? '—'}{r.negatives != null ? ` (${r.negatives} bg)` : ''}</Td>
                                                <Td fontSize="xs" fontFamily="mono">{r.version || '—'}</Td>
                                                <Td fontSize="xs">{pct(metricOf(r.metrics, 'map50'))} / {pct(metricOf(r.champion_metrics, 'map50'))}</Td>
                                                <Td fontSize="xs" maxW="260px">
                                                    {r.promoted ? <Badge colorScheme="green" mr={1}><Icon as={MdCheckCircle} mr={0.5} />promoted</Badge> : r.promoted === false ? <Badge mr={1}>kept production</Badge> : null}
                                                    <Text as="span" color={muted} noOfLines={2}>{r.error || r.decision_reason || ''}</Text>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        )}
                    </Box>

                    <Accordion allowToggle>
                        <AccordionItem border="none">
                            <AccordionButton px={0}><Box flex={1} textAlign="left" fontSize="sm" fontWeight="800">How the detection model learns</Box><AccordionIcon /></AccordionButton>
                            <AccordionPanel px={0} fontSize="xs" color={muted}>
                                <VStack align="stretch" spacing={1.5}>
                                    <Text><b>1. Drive Mode</b> — the phone sends each photo it is sure about, with the AI's boxes, GPS, road and segment.</Text>
                                    <Text><b>2. Review</b> — open a photo and press Confirm, pick the right damage type, or Not damage. Technician "Teach AI" photos are already labelled.</Text>
                                    <Text><b>3. Train</b> — the ML engine trains on all labelled photos ("not damage" photos teach it what to ignore), then scores the new model on photos it has never seen.</Text>
                                    <Text><b>4. Promote</b> — the new model replaces production only if its mAP50 is not worse. The run table shows each decision.</Text>
                                    <Text><b>5. Segment health</b> — confirmed damage updates each segment's potholes / cracking and the segment PCI is re-scored.</Text>
                                </VStack>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                </VStack>
            )}
        </Box>
    );
};

RoadVisionModelPanel.propTypes = { variant: PropTypes.oneOf(['full', 'mini']), embedded: PropTypes.bool };

export default memo(RoadVisionModelPanel);
